import {
  Activity,
  AlertTriangle,
  Archive,
  BadgeCheck,
  BadgeInfo,
  BellRing,
  BookOpen,
  Briefcase,
  Building2,
  CalendarDays,
  ChefHat,
  ClipboardCheck,
  CoinsIcon,
  CreditCard,
  FlaskConical,
  HardHat,
  HeadphonesIcon,
  Landmark,
  LayoutDashboard,
  Mail,
  MessageSquare,
  Newspaper,
  PieChart,
  Settings,
  ShieldCheck,
  Tag,
  Terminal,
  UserCheck,
  UserSquare2,
  Users,
  Users2,
  Wallet,
  Wrench,
} from "lucide-react";

export interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

/**
 * The dashboard's navigation, grouped.
 *
 * This was a flat array of thirty links. On a desktop sidebar that is merely
 * long; in an off-canvas drawer on a phone it is a scroll through thirty
 * entries to reach the one you want, every time you switch pages.
 *
 * Grouped by what an operator is doing rather than by data model — "Work
 * queues" is the set of things with someone waiting on the other end, which is
 * the distinction that matters when you are triaging on a phone.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    title: "Overview",
    items: [
      { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
    ],
  },
  {
    title: "Work queues",
    items: [
      { label: "Bookings", path: "/bookings", icon: BookOpen },
      { label: "Jobs", path: "/jobs", icon: Briefcase },
      { label: "CSR Tickets", path: "/csr", icon: HeadphonesIcon },
      { label: "Complaints", path: "/complaints", icon: AlertTriangle },
      { label: "Conversations", path: "/conversations", icon: MessageSquare },
      { label: "Inspections", path: "/inspections", icon: ClipboardCheck },
    ],
  },
  {
    title: "Verification",
    items: [
      { label: "Verification Orders", path: "/verification-orders", icon: ShieldCheck },
      { label: "Verif. Services", path: "/verification-services", icon: BadgeInfo },
      { label: "Lab Test Orders", path: "/lab-test-orders", icon: FlaskConical },
      { label: "Guarantors", path: "/guarantors", icon: UserSquare2 },
    ],
  },
  {
    title: "People",
    items: [
      { label: "Users", path: "/apps-users-list", icon: Users },
      { label: "Workers", path: "/myndas", icon: HardHat },
      { label: "Employers", path: "/employers", icon: UserCheck },
      { label: "Agencies", path: "/agencies", icon: Landmark },
      { label: "Laboratories", path: "/laboratories", icon: Building2 },
      { label: "Placements", path: "/placements", icon: Users2 },
      { label: "Archived Users", path: "/archived-users", icon: Archive },
    ],
  },
  {
    title: "Money",
    items: [
      { label: "Transactions", path: "/transactions", icon: CoinsIcon },
      { label: "Wallets", path: "/wallets", icon: Wallet },
      { label: "Subscription Plans", path: "/subscription-plans", icon: CreditCard },
      { label: "Service Plans", path: "/service-plans", icon: Wrench },
      { label: "Job Plans", path: "/job-plans", icon: BadgeCheck },
    ],
  },
  {
    title: "Content & comms",
    items: [
      { label: "Notifications", path: "/notifications", icon: BellRing },
      { label: "Meetings", path: "/events", icon: CalendarDays },
      { label: "Blogs", path: "/blogs", icon: Newspaper },
    ],
  },
  {
    title: "Configuration",
    items: [
      { label: "Service Category", path: "/service-category", icon: ChefHat },
      { label: "Roles", path: "/roles", icon: Tag },
      { label: "Activity Logs", path: "/activities", icon: Activity },
      { label: "User Activity", path: "/user-activity", icon: PieChart },
      { label: "Email Logs", path: "/email-logs", icon: Mail },
      { label: "API Activity", path: "/api-activity-logs", icon: Terminal },
      { label: "Settings", path: "/settings", icon: Settings },
    ],
  },
];

export const NAV_ITEMS: NavItem[] = NAV_SECTIONS.flatMap(
  (section) => section.items
);

/**
 * The four destinations that earn a permanent slot on a phone.
 *
 * Deliberately the queues an operator opens to *check* on, not the ones they
 * arrive at by following a link. Withdrawal approvals are the clearest example
 * of the latter: ops-alert deep-links straight to the specific failed
 * withdrawal via `?focus=`, so a Transactions tab would mostly duplicate a
 * journey that already works.
 */
export const TAB_PATHS = [
  "/dashboard",
  "/bookings",
  "/csr",
  "/complaints",
] as const;

/** Short forms, because a tab label has about ten characters to work with. */
const TAB_LABELS: Record<string, string> = {
  "/dashboard": "Home",
  "/bookings": "Bookings",
  "/csr": "CSR",
  "/complaints": "Issues",
};

export const TAB_ITEMS: NavItem[] = TAB_PATHS.map((path) => {
  const item = NAV_ITEMS.find((candidate) => candidate.path === path)!;
  return { ...item, label: TAB_LABELS[path] ?? item.label };
});

/**
 * Whether a nav entry should read as the current page.
 *
 * Dashboard is matched exactly: every path starts with "/", so a prefix test
 * would light it up everywhere.
 */
export const isActivePath = (itemPath: string, pathname: string): boolean =>
  itemPath === "/dashboard"
    ? pathname === itemPath
    : pathname === itemPath || pathname.startsWith(`${itemPath}/`);

/**
 * The page name for the header.
 *
 * Replaces a hand-maintained map that covered 17 of the 30 routes — complaints,
 * csr, verification orders and eleven others fell through to "Mynda Admin". On
 * desktop that is cosmetic; on a phone the header is the only thing telling you
 * where you are, because the sidebar is off-canvas.
 */
const EXTRA_TITLES: Record<string, string> = {
  "/job-details": "Job Details",
  "/create-job": "Create Job",
  "/edit-job": "Edit Job",
  "/invoice": "Invoice",
  "/send-invoice": "Send Invoice",
  "/pages-account": "Account",
  "/user-profile": "Profile",
  "/skills": "Skills",
};

export const pageTitleFor = (pathname: string): string => {
  const match = NAV_ITEMS.find((item) => isActivePath(item.path, pathname));
  if (match) return match.label;

  const extra = Object.entries(EXTRA_TITLES).find(([path]) =>
    pathname.startsWith(path)
  );
  return extra ? extra[1] : "Mynda Admin";
};
